import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import { useToast } from '@/components/base/Toast';
import { useAuth } from '@/hooks/useAuth';

// ---------- Tipos ----------
interface Comentario {
  id: string;
  autor: string;
  texto: string;
  fecha: string;
}

type EstadoNota = 'pendiente' | 'trabajo' | 'revisado' | 'completado';

interface Nota {
  id: string;
  contenido: string;
  dirigidoA: string;
  color: string;
  fecha: string;
  estado: EstadoNota;
  creadorId: string;
  creadorNombre: string;
  comentarios: Comentario[];
}

// ---------- Constantes ----------
const colorMap: Record<string, string> = {
  amber: 'border-l-amber-400 bg-amber-50/60',
  red: 'border-l-red-400 bg-red-50/60',
  green: 'border-l-emerald-400 bg-emerald-50/60',
  slate: 'border-l-slate-400 bg-slate-50/60',
};

const estadoLabels: Record<EstadoNota, string> = {
  pendiente: 'Pendiente',
  trabajo: 'En trabajo',
  revisado: 'Revisado',
  completado: 'Completado',
};

const estadoColors: Record<EstadoNota, string> = {
  pendiente: 'bg-amber-100 text-amber-700',
  trabajo: 'bg-blue-100 text-blue-700',
  revisado: 'bg-emerald-100 text-emerald-700',
  completado: 'bg-slate-200 text-slate-600',
};

const colorOptions = [
  { value: 'amber', label: 'Ámbar', icon: 'ri-circle-fill text-amber-400' },
  { value: 'red', label: 'Rojo', icon: 'ri-circle-fill text-red-400' },
  { value: 'green', label: 'Verde', icon: 'ri-circle-fill text-emerald-400' },
  { value: 'slate', label: 'Gris', icon: 'ri-circle-fill text-slate-400' },
];

// ---------- API helpers ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function NotesPanel({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { showToast } = useToast();
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'admin';
  const miNombre = usuario?.nombre || usuario?.email || 'Usuario';

  const [notas, setNotas] = useState<Nota[]>([]);
  const [loading, setLoading] = useState(true);
  const [nuevaNota, setNuevaNota] = useState('');
  const [quickDirigidoA, setQuickDirigidoA] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [notaForm, setNotaForm] = useState({ contenido: '', dirigidoA: '', color: 'amber' });

  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentTarget, setCommentTarget] = useState<Nota | null>(null);
  const [nuevoComentario, setNuevoComentario] = useState('');

  const [usuariosLista, setUsuariosLista] = useState<{ id: string; nombre: string; rol: string }[]>([]);

  // Cargar notas y usuarios
  const loadNotas = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/notas/');
      setNotas(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar notas', err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsuarios = async () => {
    if (!esAdmin) return;
    try {
      const data = await fetchWithAuth('/usuarios/');
      setUsuariosLista(data.map((u: any) => ({ id: u.id, nombre: u.nombre, rol: u.rol })));
    } catch {
      // no crítico
    }
  };

  useEffect(() => {
    loadNotas();
    loadUsuarios();
  }, []);

  // ---------- Handlers ----------
  const agregarNotaModal = async () => {
    if (!notaForm.contenido.trim()) return;
    try {
      const nueva = await fetchWithAuth('/notas/', {
        method: 'POST',
        body: JSON.stringify({
          contenido: notaForm.contenido.trim(),
          dirigidoA: notaForm.dirigidoA.trim() || (esAdmin ? (usuariosLista[0]?.nombre || '') : miNombre),
          color: notaForm.color,
        }),
      });
      setNotas(prev => [nueva, ...prev]);
      showToast('success', 'Nota creada', `Dirigida a ${nueva.dirigidoA}.`);
      setModalOpen(false);
      setNotaForm({ contenido: '', dirigidoA: esAdmin ? (usuariosLista[0]?.nombre || '') : miNombre, color: 'amber' });
    } catch (err: any) {
      showToast('error', 'Error al crear nota', err.message);
    }
  };

  const agregarNotaRapida = async () => {
    if (!nuevaNota.trim()) return;
    const destino = esAdmin ? (quickDirigidoA || 'Todo el equipo') : miNombre;
    try {
      const nueva = await fetchWithAuth('/notas/', {
        method: 'POST',
        body: JSON.stringify({
          contenido: nuevaNota.trim(),
          dirigidoA: destino,
          color: 'amber',
        }),
      });
      setNotas(prev => [nueva, ...prev]);
      setNuevaNota('');
      showToast('success', 'Nota rápida', `Dirigida a ${destino}.`);
    } catch (err: any) {
      showToast('error', 'Error al crear nota', err.message);
    }
  };

  const eliminarNota = async (nota: Nota) => {
    const puedeEliminar = esAdmin || nota.creadorId === usuario?.id;
    if (!puedeEliminar) {
      showToast('warning', 'Sin permisos', 'Solo el creador o un administrador pueden eliminar esta nota.');
      return;
    }
    try {
      await fetchWithAuth(`/notas/${nota.id}`, { method: 'DELETE' });
      setNotas(prev => prev.filter(n => n.id !== nota.id));
      showToast('success', 'Nota eliminada');
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  const openComments = (nota: Nota) => {
    setCommentTarget(nota);
    setNuevoComentario('');
    setCommentModalOpen(true);
  };

  const agregarComentario = async () => {
    if (!nuevoComentario.trim() || !commentTarget) return;
    try {
      const updated = await fetchWithAuth(`/notas/${commentTarget.id}/comentarios`, {
        method: 'POST',
        body: JSON.stringify({ texto: nuevoComentario.trim() }),
      });
      setNotas(prev => prev.map(n => n.id === updated.id ? updated : n));
      setCommentTarget(updated);
      setNuevoComentario('');
    } catch (err: any) {
      showToast('error', 'Error al agregar comentario', err.message);
    }
  };

  const cambiarEstado = async (nuevoEstado: EstadoNota) => {
    if (!commentTarget) return;
    if (!esAdmin && nuevoEstado !== 'trabajo') {
      showToast('warning', 'Sin permisos', 'Solo el administrador puede cambiar a ese estado.');
      return;
    }
    try {
      const updated = await fetchWithAuth(`/notas/${commentTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      setNotas(prev => prev.map(n => n.id === updated.id ? updated : n));
      setCommentTarget(updated);
      showToast('success', 'Estado actualizado');
    } catch (err: any) {
      showToast('error', 'Error al cambiar estado', err.message);
    }
  };

  const estadosDisponibles: EstadoNota[] = esAdmin
    ? ['pendiente', 'trabajo', 'revisado', 'completado']
    : ['trabajo'];

  const openModal = () => {
    setNotaForm({
      contenido: '',
      dirigidoA: esAdmin ? (usuariosLista[0]?.nombre || '') : miNombre,
      color: 'amber',
    });
    setModalOpen(true);
  };

  return (
    <>
      <aside
        className={`fixed right-0 top-0 h-full bg-background-50 border-l border-background-200 flex flex-col z-30 transition-all duration-300 ${
          collapsed ? 'w-0 border-l-0 overflow-hidden' : 'w-[300px]'
        }`}
      >
        {/* Encabezado */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-sticky-note-line text-foreground-600" />
            <h3 className="font-heading font-semibold text-sm text-foreground-800">Notas rápidas</h3>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={openModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600">
              <i className="ri-sticky-note-add-line text-sm" />
            </button>
            <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>

        {/* Input rápido */}
        <div className="p-3 border-b border-background-200">
          {esAdmin ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nuevaNota}
                  onChange={(e) => setNuevaNota(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && agregarNotaRapida()}
                  placeholder="Escribe una nota rápida..."
                  className="flex-1 px-3 py-1.5 rounded-lg border border-background-200 bg-background-50 text-xs"
                />
                <button onClick={agregarNotaRapida} className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600">
                  <i className="ri-add-line text-sm" />
                </button>
              </div>
              <select
                value={quickDirigidoA}
                onChange={(e) => setQuickDirigidoA(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-[11px]"
              >
                <option value="">Dirigido a...</option>
                <option value="Todo el equipo">Todo el equipo</option>
                {usuariosLista.map(u => (
                  <option key={u.id} value={u.nombre}>{u.nombre}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={nuevaNota}
                onChange={(e) => setNuevaNota(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && agregarNotaRapida()}
                placeholder="Escribe una nota para ti..."
                className="flex-1 px-3 py-1.5 rounded-lg border border-background-200 bg-background-50 text-xs"
              />
              <button onClick={agregarNotaRapida} className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600">
                <i className="ri-add-line text-sm" />
              </button>
            </div>
          )}
        </div>

        {/* Lista de notas */}
        <div className="flex-1 overflow-y-auto notes-scrollbar p-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><i className="ri-loader-4-line animate-spin text-foreground-400" /></div>
          ) : notas.length === 0 ? (
            <div className="text-center py-8 text-xs text-foreground-400">No hay notas aún.</div>
          ) : (
            notas.map(nota => {
              const puedeEliminar = esAdmin || nota.creadorId === usuario?.id;
              return (
                <div key={nota.id} className={`p-3 rounded-lg border-l-[3px] relative group transition-all ${colorMap[nota.color] || colorMap.slate}`}>
                  <p className="text-xs text-foreground-700 leading-relaxed pr-5">{nota.contenido}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <p className="text-[10px] text-foreground-400">{nota.fecha}</p>
                    <span className="text-[10px] text-foreground-400">&middot;</span>
                    <span className="text-[10px] text-foreground-500 font-medium">{nota.dirigidoA}</span>
                    <span className="text-[10px] text-foreground-400">&middot;</span>
                    <span className="text-[10px] text-foreground-400">por {nota.creadorNombre}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${estadoColors[nota.estado]}`}>{estadoLabels[nota.estado]}</span>
                    {nota.comentarios.length > 0 && (
                      <>
                        <span className="text-[10px] text-foreground-400">&middot;</span>
                        <span className="text-[10px] text-foreground-400">{nota.comentarios.length} coment.</span>
                      </>
                    )}
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => openComments(nota)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-black/5 text-foreground-400 hover:text-primary-600">
                      <i className="ri-chat-3-line text-xs" />
                    </button>
                    {puedeEliminar && (
                      <button onClick={() => eliminarNota(nota)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-black/5 text-foreground-400 hover:text-red-500">
                        <i className="ri-close-line text-xs" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Modal crear nota */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nueva nota"
        size="sm"
        footer={
          <>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-background-200 text-sm">Cancelar</button>
            <button onClick={agregarNotaModal} disabled={!notaForm.contenido.trim()} className={`px-5 py-2 rounded-lg text-sm ${!notaForm.contenido.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
              Crear nota
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <textarea value={notaForm.contenido} onChange={(e) => setNotaForm({ ...notaForm, contenido: e.target.value })} rows={4} placeholder="Escribe el contenido..." className="w-full px-3 py-2 rounded-lg border" />
          <div>
            <label className="block text-xs font-medium mb-1.5">Dirigido a</label>
            {esAdmin ? (
              <select value={notaForm.dirigidoA} onChange={(e) => setNotaForm({ ...notaForm, dirigidoA: e.target.value })} className="w-full px-3 py-2 rounded-lg border">
                <option value="">Seleccionar...</option>
                <option value="Todo el equipo">Todo el equipo</option>
                {usuariosLista.map(u => (
                  <option key={u.id} value={u.nombre}>{u.nombre} ({u.rol})</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={miNombre}
                readOnly
                className="w-full px-3 py-2 rounded-lg border bg-background-100 text-sm text-foreground-500 cursor-not-allowed"
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium mb-2">Color</label>
            <div className="flex gap-2">
              {colorOptions.map(opt => (
                <button key={opt.value} onClick={() => setNotaForm({ ...notaForm, color: opt.value })} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border ${notaForm.color === opt.value ? 'border-foreground-300 bg-background-100' : 'border-background-200'}`}>
                  <i className={opt.icon} />{opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal comentarios */}
      <Modal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        title={commentTarget ? `Comentarios: ${commentTarget.contenido.slice(0, 40)}...` : 'Comentarios'}
        size="md"
        footer={null}
      >
        {commentTarget && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 bg-background-100 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">Estado:</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${estadoColors[commentTarget.estado]}`}>{estadoLabels[commentTarget.estado]}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-foreground-400">Cambiar a:</span>
                {estadosDisponibles.map(est => (
                  <button key={est} onClick={() => cambiarEstado(est)} className={`text-[10px] px-2 py-1 rounded-full font-medium border ${commentTarget.estado === est ? 'border-foreground-400 bg-foreground-100' : 'border-background-200'}`}>
                    {estadoLabels[est]}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {commentTarget.comentarios.length === 0 ? (
                <div className="text-center py-8 text-xs text-foreground-400">Sin comentarios.</div>
              ) : (
                commentTarget.comentarios.map(cm => (
                  <div key={cm.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold">{cm.autor.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold">{cm.autor}</span>
                        <span className="text-[10px] text-foreground-400">{cm.fecha}</span>
                      </div>
                      <p className="text-xs text-foreground-600">{cm.texto}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <input
                type="text"
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && agregarComentario()}
                placeholder="Escribe un comentario..."
                className="flex-1 px-3 py-2 rounded-lg border"
              />
              <button onClick={agregarComentario} disabled={!nuevoComentario.trim()} className={`px-4 py-2 rounded-lg text-sm ${!nuevoComentario.trim() ? 'bg-background-200 cursor-not-allowed' : 'bg-primary-500 text-background-50'}`}>
                <i className="ri-send-plane-fill" />
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}